import { environment } from 'environments';
import { convertMetricToImperialHeight } from '@shared-utils';

(() => {
  class VisitQuestionsController {
    constructor($scope, Orders, Logger, Utils, QuestionnaireService, Constants) {
      'ngInject';

      Object.assign(this, {
        Orders,
        Logger,
        Utils,
        QuestionnaireService,
        Constants,
        environment: environment.constants.ENV,
        BOD_557_feature_flag: ['dev', 'localhost', 'qa', 'staging', 'production'].includes(environment.constants.ENV),
        LMNUK_1695: ['localhost', 'dev', 'qa', 'staging', 'production'].includes(environment.constants.ENV),
        BOD_1144: ['localhost', 'dev', 'qa', 'staging', 'production'].includes(environment.constants.ENV),
        // CONSTANTS FOR UNIT CONVERSION
        INCH_TO_CM_CONVERSION_RATE: 2.54,
        CM_TO_INCH_CONVERSION_RATE: 0.393701,
        STONE_TO_KILOGRAM_CONVERSION_RATE: 6.35029,
        POUNDS_TO_KILOGRAM_CONVERSION_RATE: 0.453592,
        KILOGRAM_TO_POUNDS_CONVERSION_RATE: 2.20462,
        BOD_2170: ['localhost'].includes(environment.constants.ENV),
        BOD_2857: ['localhost', 'dev', 'qa', 'staging', 'production'].includes(environment.constants.ENV),
        dateOptions: {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hourCycle: 'h12',
        },
      });

      $scope.$on('tab:selected:questions', this.init.bind(this));
    }

    init() {
      if (this.visit.answer_score_1 == null) {
        this.visit.answer_score_1 = 0;
      }
      if (this.visit.answer_score_2 == null) {
        this.visit.answer_score_2 = 0;
      }

      this.loading = true;
      this.Orders.getResponses()
        .then(({ data }) => {
          this.Logger.log('Questions: ', data);

          this.error = null;
          this.answers = data.answers;
          this.alerts = this.Orders.getQuestionnaireAlerts(data, this.patient);
          this.initialized = true;
          this.bmi = 0;
          this.bmi_weight = 0;
          this.stones = 0;
          this.pounds = 0;
          this.kilograms = 0;
          this.inches = 0;
          this.feet = 0;
          this.centimeters = 0;

          let index = 0;

          // Hack to avoid another API change as we disscused with Greg
          this.answers.forEach(answer => {
            if (!answer.question.display_condition) {
              answer.parentQuestionIndex = index + 1;
              index++;
            }

            if (
              [
                'bmi',
                'bmi_weight',
                'bmi_cocp_nor',
                'bmi_pde_pro',
                'bmi_map_lev1',
                'bmi_map_lev2',
                'bmi_map_uli',
                'bmi_wei',
                'bmi_meno',
                'bmi_wei_r',
              ].includes(answer.question.type)
            ) {
              if (answer.question.type === 'bmi') {
                this.bmi = this.calculateBMI(answer.value.height, answer.value.weight, answer.value.system);
              } else {
                this.bmi_weight = this.calculateBMI(answer.value.height, answer.value.weight, answer.value.system);
              }

              if (answer.value.system === 'imperial') {
                //  convert inches to feet and inches
                //  https://23andme.atlassian.net/browse/LMNUK-150
                this.imperialHeight = this.convertToFeetAndInches(answer.value.height);
                this.stones = Math.floor(answer.value.weight / 14);
                this.pounds = Math.round(answer.value.weight % 14);
                this.kilograms = this.convertToKilograms(this.stones, this.pounds);
                this.centimeters = this.BOD_2170
                  ? Math.round(answer.value.height * this.Constants.UnitConversionRate.INCH_TO_CM_CONVERSION_RATE)
                  : Math.round(answer.value.height * this.INCH_TO_CM_CONVERSION_RATE);
              }

              if (answer.value.system === 'metric') {
                this.centimeters = Math.round(answer.value.height);
                this.kilograms = Math.round(answer.value.weight);
                const { stones, pounds } = this.convertToStoneAndPounds(answer.value.weight);
                this.stones = stones;
                this.pounds = pounds;
                if (this.BOD_2857) {
                  this.imperialHeight = convertMetricToImperialHeight(this.centimeters);
                } else {
                  this.imperialHeight = this.BOD_2170
                    ? this.convertToFeetAndInches(
                        this.centimeters * this.Constants.UnitConversionRate.CM_TO_INCH_CONVERSION_RATE,
                      )
                    : this.convertToFeetAndInches(this.centimeters * this.CM_TO_INCH_CONVERSION_RATE);
                }
              }
            }

            if (answer.question.type === 'text') {
              if (!this.Utils.isArray(answer.value) && this.Utils.isArray(answer.question.options)) {
                const value = {};
                value.text = answer.value;
                value.status = answer.question.options[0].status;
                answer.value = value;
                // answer.value = [];
                // answer.value.push(value);
              }
            }

            if (this.BOD_1144 && answer.question.type === 'date') {
              answer.value.text = new Date(answer.value.date);
            }

            if (answer.question.type === 'select') {
              if (answer.details && this.Utils.isJson(answer.details)) {
                const details = JSON.parse(answer.details);

                if (this.Utils.isArray(answer.value) && answer.value.length) {
                  answer.value.forEach(option => {
                    if (details[option.id]) {
                      option.details = details[option.id];
                    }
                  });
                }
              } else if (answer.details) {
                if (this.Utils.isArray(answer.value) && answer.value.length) {
                  answer.value.forEach(option => {
                    if (answer.details) {
                      option.details = answer.details;
                    }
                  });
                }
              }
            }

            if (answer.question.type === 'fast_track') {
              let value;

              if (answer.value) {
                const details = JSON.parse(answer.details);

                answer.question.options.forEach(option => {
                  if (option.text.toLowerCase() === 'yes') {
                    value = option;
                  }
                });
                value.details = details[value.id];
                answer.value = [value];
              } else {
                answer.question.options.forEach(option => {
                  if (option.text.toLowerCase() === 'no') {
                    value = option;
                  }
                });
                answer.value = [value];
              }
            }

            if (['gp_address'].includes(answer.question.type)) {
              if (answer.value) {
                answer.value = answer.value.map(value => ({
                  text:
                    answer.details && answer.details[value]
                      ? answer.question.options.find(option => option.id === value).text +
                        ': ' +
                        this.buildRoadName(answer.details[value])
                      : answer.question.options.find(option => option.id === value).text,
                  status: answer.question.options.find(option => option.id === value).status,
                }));
              }
            }

            if (['gp_address_csv', 'gp_address_docman'].includes(answer.question.type)) {
              if (answer.value) {
                answer.value = answer.value.map(value => {
                  const answer_text = answer.details
                    ? `${answer.question.options.find(option => option.id === value).text}: ${this.gpAddressStringBuilder(answer.details[value])}`
                    : `${answer.question.options.find(option => option.id === value).text}`;

                  return {
                    text: answer_text,
                    status: answer.question.options.find(option => option.id === value).status,
                  };
                });
              }
            }
          });
        })
        .catch(err => {
          if (err.status === 404) {
            this.error = 'No questionnaire uploaded';
          } else {
            this.error = 'Could not load questionnaire';
          }
        })
        .finally(() => {
          this.loading = false;
        });
    }

    gpAddressStringBuilder = answer =>
      typeof answer === 'string'
        ? answer
        : [answer?.surgery_name, this.streetStringBuilder(answer), answer?.town, answer?.postcode].reduce(
            (acc, item) => (acc && item ? `${acc}, ${item}` : item || acc),
            '',
          );

    streetStringBuilder = ({ line_1, line_2, line_3, line_4, line_5 }) =>
      [line_1, line_2, line_3, line_4, line_5].reduce(
        (acc, item) => (acc && item ? `${acc}, ${item}` : item || acc),
        '',
      );

    prescribeTabShortcut($event) {
      const TAB_NAME = 'Prescribe';
      const prescribeTabIndex = this.parent.tabs.findIndex(item => item.name === TAB_NAME);
      if (prescribeTabIndex !== -1) {
        this.parent.selectTab(TAB_NAME, prescribeTabIndex, $event);
      }
    }

    buildRoadName(item) {
      let road = '';
      road += item.surgery_name;
      road += `, ${item.line_1}`;
      road += item.line_2 ? `, ${item.line_2}` : '';
      road += item.line_3 ? `, ${item.line_3}` : '';
      road += `, ${item.postcode}`;
      return road;
    }

    replaceDotForDash(warning_level) {
      return this.Utils.replaceDotForDash(warning_level);
    }

    hasDetails(responseId, answerId) {
      let hasDetail = false;

      for (let i = 0; i < this.answers.length; i += 1) {
        const response = this.answers[i];

        if (response.id === responseId) {
          for (let j = 0; j < response.question.options.length; j += 1) {
            const option = response.question.options[j];

            if (option.id === answerId) {
              if (option.details) {
                hasDetail = true;
              }
              break;
            }
          }

          break;
        }
      }

      return hasDetail;
    }

    truncate(word, n) {
      return word.substring(0, n) + '...';
    }

    calculateBMI(height, weight, system) {
      let bmi;
      if (system === 'metric') {
        bmi = (weight / (height * height)) * 10000;
      } else {
        bmi = (703 * weight) / (height * height);
      }

      return bmi.toFixed(1);
    }

    convertToFeetAndInches(heightInInches) {
      return {
        feet: Math.floor(heightInInches / 12),
        inches: Math.round(heightInInches % 12) > 11 ? 11 : Math.round(heightInInches % 12),
      };
    }

    convertToKilograms(stones, pounds) {
      return this.BOD_2170
        ? Math.round(
            stones * this.Constants.UnitConversionRate.STONE_TO_KILOGRAM_CONVERSION_RATE +
              pounds * this.Constants.UnitConversionRate.POUNDS_TO_KILOGRAM_CONVERSION_RATE,
          )
        : Math.round(
            stones * this.STONE_TO_KILOGRAM_CONVERSION_RATE + pounds * this.POUNDS_TO_KILOGRAM_CONVERSION_RATE,
          );
    }

    convertToStoneAndPounds(kilograms) {
      const oldConversion = {
        stones: Math.floor(kilograms / this.STONE_TO_KILOGRAM_CONVERSION_RATE),
        pounds: Math.round(
          (kilograms / this.STONE_TO_KILOGRAM_CONVERSION_RATE -
            Math.floor(kilograms / this.STONE_TO_KILOGRAM_CONVERSION_RATE)) *
            14,
        ),
      };
      const newConversion = {
        stones: Math.floor(kilograms / this.Constants.UnitConversionRate.STONE_TO_KILOGRAM_CONVERSION_RATE),
        pounds: Math.round(
          (kilograms / this.Constants.UnitConversionRate.STONE_TO_KILOGRAM_CONVERSION_RATE -
            Math.floor(kilograms / this.Constants.UnitConversionRate.STONE_TO_KILOGRAM_CONVERSION_RATE)) *
            14,
        ),
      };
      return this.BOD_2170 ? newConversion : oldConversion;
    }

    // getScanWellLabs(visit_id){

    //   this.Orders.getPartnerLabs(visit_id)
    //     .then(({ data }) => {
    //       this.scanWellResult = data;

    //       this.error = null;
    //       this.initialized = true;

    //       var obj = JSON.parse(data[0].payload);

    //     })
    //     .catch((err) => {
    //       if (err.status === 404) {
    //         this.error = 'No data found';
    //       } else {
    //         this.error = 'Could not load data';
    //       }
    //     })
    //     .finally(() => { this.loading = false; });
    // }
  }

  VisitQuestionsController.$inject = ['$scope', 'Orders', 'Logger', 'Utils', 'QuestionnaireService', 'Constants'];
  angular.module('pims').component('visitQuestions', {
    templateUrl: 'app/views/visits/detail/sections/questions/questions.html',
    controller: VisitQuestionsController,
    bindings: {
      currentUser: '=',
      patient: '=',
      visit: '=',
      partnerLabs: '=',
      parent: '=',
    },
  });
})();
